import { v4 as uuid } from "uuid";
import ProjectService from "../services/projectService.js";
import CloudStorageService from "../services/cloudStorageService.js";
import { useUploadMonitor, UPLOAD_STATUS } from "./useUploadMonitor.js";
import { useDocs } from "./useDocs.js";
import { useDocsByTab } from "./useDocsByTab.js";
import { useProjectDocs } from "./useProjectDocs.js";
import { useProjectsByTab } from "./useProjectsByTab.js";

const MAX_FILE_SIZE = 200_000_000; // 200MB
const CONCURRENCY_LIMIT = 5;

// No project selected — the backend files these under the user's default,
// unfiled-documents bucket rather than a real project.
export const UNFILED_PROJECT_ID = "SINGLETON";

const projectService = new ProjectService();
const cloudStorageService = new CloudStorageService();

// Guards the polling loop so overlapping uploadDocuments() calls don't spawn
// more than one concurrent "watch for PENDING uploads" chain.
let isWatchingStatus = false;

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useDocumentUpload() {
  const { files, addFiles, updateStatus } = useUploadMonitor();

  function uploadDocuments(projectId, fileList) {
    const targetProjectId = projectId ?? UNFILED_PROJECT_ID;

    const entries = Array.from(fileList).map((file) => {
      const id = uuid();
      if (file.size > MAX_FILE_SIZE) {
        return { id, projectId: targetProjectId, name: file.name, mimeType: file.type, status: UPLOAD_STATUS.ERROR, message: "EXCEEDS_SIZE" };
      }
      return { id, projectId: targetProjectId, name: file.name, mimeType: file.type, status: UPLOAD_STATUS.JUST_ADDED, file };
    });

    addFiles(entries);
    const pending = entries.filter((e) => e.status === UPLOAD_STATUS.JUST_ADDED);
    startUploading(pending).catch(console.error);
  }

  // Files picked from a connected cloud storage (Google Drive/OneDrive/etc.)
  // — unlike local files, nothing is read/uploaded client-side. The backend
  // fetches the file directly from the provider using the stored token; we
  // only need to hand it the provider's file id per document.
  function uploadCloudDocuments(projectId, storageId, cloudFiles) {
    const targetProjectId = projectId ?? UNFILED_PROJECT_ID;
    const entries = cloudFiles.map((f) => ({
      id: uuid(), projectId: targetProjectId, name: f.name, cloudFileId: f.id, status: UPLOAD_STATUS.UPLOADING,
    }));

    addFiles(entries);
    startCloudUpload(targetProjectId, storageId, entries).catch(console.error);
  }

  async function startCloudUpload(projectId, storageId, entries) {
    try {
      const cloudStorageFileIds = entries.map((e) => e.cloudFileId);
      const documentIds = entries.map((e) => e.id);
      const results = await cloudStorageService.uploadDocuments(projectId, storageId, cloudStorageFileIds, documentIds);
      // The backend doesn't reliably echo back the documentId we submitted
      // (confirmed against the legacy app's own handling of this mutation,
      // which only trusts the returned documentId for per-item errors, not
      // success) — correlate by submission order first, since results come
      // back in the same order as documentIds/cloudStorageFileIds went in.
      (results ?? []).forEach((result, index) => {
        const entry = entries[index] ?? entries.find((e) => e.id === result.documentId);
        if (entry) applyResult(entry, result);
      });
      watchForPendingStatus();
    } catch (error) {
      entries.forEach((e) => updateStatus(e.id, UPLOAD_STATUS.ERROR, error?.message ?? "UPLOAD_FAILED"));
    }
  }

  async function startUploading(entries) {
    entries.forEach((e) => updateStatus(e.id, UPLOAD_STATUS.UPLOADING));

    for (let i = 0; i < entries.length; i += CONCURRENCY_LIMIT) {
      const batch = entries.slice(i, i + CONCURRENCY_LIMIT);
      await Promise.all(batch.map(uploadOne));
    }

    watchForPendingStatus();
  }

  async function uploadOne(entry) {
    try {
      let uploadPath = null;
      try {
        uploadPath = await projectService.uploadDocumentMp({ file: entry.file });
      } catch {
        uploadPath = null; // fall back to inline base64 below
      }

      const payload = {
        projectId: entry.projectId,
        filename: entry.name,
        mimeType: entry.mimeType,
      };
      if (uploadPath) {
        payload.uploadPath = uploadPath;
      } else {
        payload.base64content = await readAsDataURL(entry.file);
      }

      const result = await projectService.uploadDocument(payload);
      applyResult(entry, result);
    } catch (error) {
      updateStatus(entry.id, UPLOAD_STATUS.ERROR, error?.message ?? "UPLOAD_FAILED");
    }
  }

  function applyResult(entry, processingStatus) {
    const type = processingStatus?.__typename;
    if (type === "DocumentProcessingSuccess") {
      updateStatus(entry.id, UPLOAD_STATUS.DONE, "", processingStatus.documentId);
      refreshTargetDocs(entry);
    } else if (type === "DocumentProcessingError") {
      updateStatus(entry.id, UPLOAD_STATUS.ERROR, processingStatus.message, processingStatus.documentId);
    } else {
      updateStatus(entry.id, UPLOAD_STATUS.PENDING, "", processingStatus?.documentId);
    }
  }

  // A no-project upload lands in the Docs view (singleton-project documents);
  // a project upload lands in that project's document list. Silently refresh
  // whichever one applies so the new upload shows up without a manual reload.
  // Docs has two independent module-level lists (the sidebar panel's flat
  // list, and the main page's tabbed one) — refresh both.
  function refreshTargetDocs(entry) {
    if (entry.projectId === UNFILED_PROJECT_ID) {
      useDocs().refresh();
      useDocsByTab().refresh();
    } else {
      useProjectDocs().refresh(entry.projectId);
      // The Projects list caches each project's document count separately
      // from its own document list — refetch it too, so the card's count
      // isn't stale next time it's shown.
      useProjectsByTab().refreshAll();
    }
  }

  // Mirrors the old app's self-terminating poll: keep checking every 2s only
  // while something is still PENDING, then stop.
  function watchForPendingStatus() {
    if (isWatchingStatus) return;
    const needsWatching = files.value.some((f) => f.status === UPLOAD_STATUS.PENDING);
    if (!needsWatching) return;

    isWatchingStatus = true;
    projectService
      .getUploadStatus()
      .then((results) => {
        for (const result of results ?? []) {
          const entry = files.value.find((f) => f.documentId === result.documentId);
          if (!entry) continue;
          if (result.__typename === "DocumentProcessingSuccess") {
            updateStatus(entry.id, UPLOAD_STATUS.DONE, "", result.documentId);
            refreshTargetDocs(entry);
          } else if (result.__typename === "DocumentProcessingError") {
            updateStatus(entry.id, UPLOAD_STATUS.ERROR, result.message, result.documentId);
          }
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        isWatchingStatus = false;
        setTimeout(watchForPendingStatus, 2000);
      });
  }

  return { uploadDocuments, uploadCloudDocuments };
}
